// Phase I3 — Agent Debate.
// Collapsible: stanced rows for each agent voice in slip.agent_debate_log.
// Three-step (Foundation → Amplifier → Max) is a property of the upstream
// pipeline; we render whatever entries exist with stance colour mapping.

import { useState } from "react";
import { summarizeReasoning } from "../../../agents/reasoning.ts";
import { Badge, Eyebrow } from "../../../components/super-acca";
import type { SuperAccaSlip } from "../../../super-acca/view-models";

export interface AgentDebateProps {
  slip: SuperAccaSlip;
  defaultOpen?: boolean;
}

export function AgentDebate({ slip, defaultOpen = false }: AgentDebateProps) {
  const [expanded, setExpanded] = useState<boolean>(defaultOpen);
  const debate = slip.agent_debate;

  if (debate.length === 0) {
    return (
      <div
        style={{
          marginTop: 28,
          border: "1px solid var(--sa-hairline)",
          background: "var(--sa-bg-panel)",
          borderRadius: 8,
          padding: 24,
        }}
      >
        <Eyebrow>The Agent Debate</Eyebrow>
        <p
          style={{
            marginTop: 10,
            fontFamily: "var(--sa-font-body)",
            fontSize: 13.5,
            color: "var(--sa-fg-secondary)",
            lineHeight: 1.6,
          }}
        >
          Debate transcript not yet recorded for this slip.
        </p>
      </div>
    );
  }

  const confirmed = debate.filter((d) => d.stance === "CONFIRM").length;
  const challenged = debate.filter((d) => d.stance === "CHALLENGE").length;

  return (
    <div
      style={{
        marginTop: 28,
        border: "1px solid var(--sa-hairline)",
        background: "var(--sa-bg-panel)",
        borderRadius: 8,
      }}
    >
      <div
        onClick={() => setExpanded(!expanded)}
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "20px 24px",
          cursor: "pointer",
          borderBottom: expanded ? "1px solid var(--sa-hairline)" : "none",
        }}
      >
        <div>
          <Eyebrow>The Agent Debate · {debate.length} Voices</Eyebrow>
          <div
            style={{
              fontFamily: "var(--sa-font-display)",
              fontSize: 22,
              fontWeight: 600,
              marginTop: 8,
              color: "var(--sa-fg-primary)",
            }}
          >
            {confirmed} confirmed · {challenged} challenged · consensus held
          </div>
        </div>
        <span
          style={{
            fontFamily: "var(--sa-font-mono)",
            fontSize: 11,
            color: "var(--sa-accent-saffron)",
            letterSpacing: "0.16em",
          }}
        >
          {expanded ? "− COLLAPSE" : "+ EXPAND DEBATE"}
        </span>
      </div>
      {expanded ? (
        <div style={{ padding: "8px 24px 24px" }}>
          {debate.map((d, i) => {
            const stanceColor =
              d.stance === "CONFIRM"
                ? "var(--sa-signal-green)"
                : d.stance === "CHALLENGE"
                  ? "var(--sa-signal-warn)"
                  : "var(--sa-accent-saffron)";
            return (
              <div
                key={`${d.agent}-${i}`}
                style={{
                  display: "grid",
                  gridTemplateColumns: "40px 1fr 110px",
                  gap: 18,
                  padding: "16px 0",
                  borderTop: i === 0 ? "none" : "1px solid var(--sa-hairline)",
                  alignItems: "flex-start",
                }}
              >
                <div
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: "50%",
                    border: `1px solid ${stanceColor}`,
                    color: stanceColor,
                    background: "var(--sa-bg-raised)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontFamily: "var(--sa-font-mono)",
                    fontWeight: 600,
                    fontSize: 12,
                  }}
                >
                  {d.agent}
                </div>
                <div>
                  <div
                    style={{
                      fontFamily: "var(--sa-font-body)",
                      fontSize: 13.5,
                      color: "var(--sa-fg-primary)",
                      fontWeight: 500,
                    }}
                  >
                    Agent {d.agent} · {d.role}
                  </div>
                  <p
                    style={{
                      fontFamily: "var(--sa-font-body)",
                      fontSize: 13,
                      color: "var(--sa-fg-secondary)",
                      marginTop: 6,
                      lineHeight: 1.6,
                    }}
                  >
                    {summarizeReasoning(d.note) ?? d.note}
                  </p>
                </div>
                <div style={{ textAlign: "right" }}>
                  <Badge
                    variant={
                      d.stance === "CONFIRM"
                        ? "success"
                        : d.stance === "CHALLENGE"
                          ? "warn"
                          : "amber"
                    }
                  >
                    {d.stance}
                  </Badge>
                </div>
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
